import type { users } from "@dropbox/api-v2-client";
import type authentication from "@mirage/service-auth/service";

export type RedirectState = {
  redirectToPathEncoded: string | null;
};

export enum ApiEnvironment {
  PROD = "prod",
  STAGE = "stage",
  VALIDATION = "validation",
}

export type DropboxAuthData = {
  sessionId: string;
  codeVerifier: string;
  connectionState: {
    service: string;
    context: string;
  };
};

export type DropboxAuthQuery = {
  state: string;
  code: string;
};

export enum AuthenticationError {
  AuthenticationNotInProgress = "authentication-not-in-progress",
  CouldNotRefreshToken = "could-not-refresh-token",
  CouldNotVerifyToken = "could-not-verify-token",
  InvalidResponse = "invalid-response",
  RefreshTokenMissing = "refresh-token-missing",
}

export enum AuthenticationStatus {
  None = "none",
  InProgress = "in-progress",
  Initializing = "initializing",
  Error = "error",
  Finishing = "finishing",
  Complete = "complete",
}

export type AuthenticationData = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  uid: string | undefined;
};

export type AccountIds = {
  uid: users.FullAccount["uid"];
  account_id: users.FullAccount["account_id"];
};

export type Service = Awaited<ReturnType<typeof authentication>>;

type LoginRedirectConfiguration = {
  redirectToPathURLParam: string;
  rootRoutePath: string;
  loginRoutePath: string;
};

export type DropboxAuthenticationConfiguration = {
  clientId: string;
  domain?: string;
  clientSecret?: string;
  oauthRedirectURL: string;
  openURL: (url: string) => void;
  loginRedirectConfig?: LoginRedirectConfiguration;
};

export type AuthenticationStorage = {
  installId: string;
  codeVerifier: string | undefined;
  accessToken: string | undefined;
  refreshToken: string | undefined;
  accessTokenExpiresAt: number | undefined;
  uid: string | undefined;
  currentAccount: users.FullAccount | undefined;
  apiEnvironment: ApiEnvironment;
  forceReauthentication: boolean;
  sessionId: string;
  shouldTraceRequests: boolean;
  shouldLogFirstInstall: boolean;
};

export type AuthenticationState = {
  status: AuthenticationStatus;
  error?: AuthenticationError;
};

export type LogoutReason =
  | "test--test"
  | `user--${"extension_menu" | "debug_menu" | "delete_account" | "logout_page" | "spinner_timeout" | "redirect_modal" | "avatar_button" | "settings_page"}`
  | `auto--${"login_failed" | "login_sync_logout" | "login_sync_code" | "eligibility_check" | "missing_refresh_token" | "invalid_grant" | "user_suspended" | "missing_scope" | "invalid_access_token"}`;
